import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { selectMatterById } from '@/matter/store/selectors'
import { selectActiveMatterExternalSyncMapping } from '@/user_files/external-storage/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { getSearchErrorMessage } from '../search-error'
import { searchMatterFiles } from '../fetch/search-matter-files'
import { searchMatterFolders } from '../fetch/search-matter-folders'
import { getSourceRootLabel } from '../source-root'
import { formatSuggestionPath } from '../suggestion-path'
import { kSuggestionsDebounceMs } from '../constants'
import type { FileSearchResult, FolderSearchResult } from '../schemas'
import { isAbortError } from '@/util/errors/is-abort-error'

export type PickerFileSearchStatus = 'loading' | 'success' | 'error'

type SearchResults = {
  /** Trimmed query these rows answer, so they are never shown under a different one. */
  query: string
  folders: FolderSearchResult[]
  files: FileSearchResult[]
  totalCount: number
  truncated: boolean
}

const EMPTY_RESULTS: SearchResults = { query: '', folders: [], files: [], totalCount: 0, truncated: false }

export type PickerFileSearch = {
  /** Current input value. */
  query: string
  setQuery: (value: string) => void
  clearSearch: () => void
  retry: () => void
  /** True while the picker lists results instead of the browsed folder. */
  isActive: boolean
  status: PickerFileSearchStatus
  error: string | null
  /** Matching folders, in the order the backend ranked them. */
  folders: FolderSearchResult[]
  /** Matching files, in the order the backend ranked them. */
  files: FileSearchResult[]
  /** Full ` / `-joined location per result id, rendered as the row's second line. */
  locationPathById: ReadonlyMap<string, string>
  /** Ancestor folder ids per result id, so navigating into a result can complete its breadcrumb. */
  ancestorFolderIdsById: ReadonlyMap<string, readonly string[]>
  /** True when the page cap stopped pagination, so the results are a prefix of the matches. */
  truncated: boolean
  /** Total matches the backend counted, used only to explain a truncated result set. */
  totalCount: number
}

/**
 * Matter-wide file and folder search for the add-files picker.
 *
 * Unlike the drive's `useMatterFilesSearch`, this runs as a live filter: the query is debounced and
 * applied as the user types, with no Enter step, suggestions dropdown, or recent searches. It also
 * takes the matter explicitly rather than reading the active matter, because the picker attaches to
 * the conversation's matter, which is not always the one the app is browsing.
 *
 * Both endpoints are matter-scoped, so results deliberately span Paxton-owned and DMS-synced files —
 * search is the "find anything in this matter" affordance, and every row carries the location it
 * came from.
 *
 * @param matterId - The matter to search within; `null` while the caller has none
 */
export function usePickerFileSearch({ matterId }: { matterId: string | null }): PickerFileSearch {
  const dispatch = useAppDispatch()
  const matterRecord = useAppSelector((state: RootState) => (matterId ? selectMatterById(state, { matterId }) : null))
  // Read, never fetched here: the picker's external tab already loads the mapping.
  const syncMapping = useAppSelector((state: RootState) => selectActiveMatterExternalSyncMapping(state, matterId))

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [status, setStatus] = useState<PickerFileSearchStatus>('success')
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS)
  const [refreshToken, setRefreshToken] = useState(0)

  const trimmedQuery = query.trim()
  // The field owns what the list is showing, so results follow the input rather than a committed
  // query. Anything typed means "show me matches", including while the debounce is still pending.
  const isActive = trimmedQuery.length > 0

  const clearSearch = useCallback(() => {
    setQuery('')
    setDebouncedQuery('')
    setResults(EMPTY_RESULTS)
    setError(null)
    setStatus('success')
  }, [])

  const retry = useCallback(() => setRefreshToken((token) => token + 1), [])

  // Typing enters the loading state immediately, so the picker never paints "no results" for a
  // query whose request has not gone out yet.
  useEffect(() => {
    if (!trimmedQuery) {
      setDebouncedQuery('')
      return
    }

    setStatus('loading')
    const timer = setTimeout(() => setDebouncedQuery(trimmedQuery), kSuggestionsDebounceMs)

    return () => clearTimeout(timer)
  }, [trimmedQuery])

  useEffect(() => {
    if (!matterId || !debouncedQuery) return

    const controller = new AbortController()
    let cancelled = false

    setStatus('loading')
    setError(null)

    Promise.all([
      searchMatterFolders(matterId, { query: debouncedQuery, signal: controller.signal }),
      searchMatterFiles(matterId, { query: debouncedQuery, signal: controller.signal }),
    ])
      .then(([folderResults, fileResults]) => {
        if (cancelled) return

        // Results can be folders the picker has never browsed to. Navigating into one builds its
        // breadcrumb from the folder store, so the record has to be there before the click.
        dispatch(UserFoldersActions.upsertFolders(folderResults.folders))

        setResults({
          query: debouncedQuery,
          folders: folderResults.folders,
          files: fileResults.files,
          totalCount: folderResults.totalCount + fileResults.totalCount,
          truncated: folderResults.truncated || fileResults.truncated,
        })
        setStatus('success')
      })
      .catch((cause) => {
        if (cancelled || isAbortError(cause)) return
        // Stamped with the query it failed for, so the error belongs to what is in the field
        // rather than reading as a stale result set and being withheld.
        setResults({ ...EMPTY_RESULTS, query: debouncedQuery })
        setError(getSearchErrorMessage(cause))
        setStatus('error')
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [matterId, debouncedQuery, refreshToken, dispatch])

  const { locationPathById, ancestorFolderIdsById } = useMemo(() => {
    const matterName = matterRecord?.name ?? 'Matter'
    const hasSyncedDrive = Boolean(syncMapping)
    const paths = new Map<string, string>()
    const ancestors = new Map<string, readonly string[]>()

    for (const item of [...results.folders, ...results.files]) {
      paths.set(item.id, formatSuggestionPath(item.folder_path, matterName, getSourceRootLabel(item, hasSyncedDrive)))
      ancestors.set(
        item.id,
        item.folder_path.map((entry) => entry.id)
      )
    }

    return { locationPathById: paths, ancestorFolderIdsById: ancestors }
  }, [results.folders, results.files, matterRecord?.name, syncMapping])

  // Rows survive in state until the next response lands, so anything that does not answer the
  // query now in the field is withheld — showing the previous query's matches under the new one's
  // header would invite the user to attach a file they never searched for.
  const answersCurrentQuery = results.query === trimmedQuery

  return {
    query,
    setQuery,
    clearSearch,
    retry,
    isActive,
    status: isActive && !answersCurrentQuery ? 'loading' : status,
    error: answersCurrentQuery ? error : null,
    folders: answersCurrentQuery ? results.folders : EMPTY_RESULTS.folders,
    files: answersCurrentQuery ? results.files : EMPTY_RESULTS.files,
    locationPathById,
    ancestorFolderIdsById,
    truncated: answersCurrentQuery && results.truncated,
    totalCount: answersCurrentQuery ? results.totalCount : 0,
  }
}
